import { ExternalCalendarEventPreview, ID } from '@griply/types';
import {
  buildEventFormSeed,
  getExternalCalendarEventRef,
  parseEventBoundary,
} from '@griply/utils';
import React, { useCallback } from 'react';
import { useOpenEditEventPopup } from 'shared/contexts/event-form';

export type ExternalEventPopupTarget = {
  externalId: ID;
  title: string;
  allDay: boolean;
  // a date key (yyyy-MM-dd) when allDay, an ISO instant otherwise; an all-day end is exclusive
  start?: string;
  end?: string;
  // absent on responses from an older backend — the caller falls back to the provider url
  connectionId?: ID;
  calendarId?: ID;
  // the event at the provider — the only place an invitee can counter-propose a time
  url?: string;
  preview?: ExternalCalendarEventPreview;
};

// Both surfaces that open an external event (the calendar card and the day-section banner
// row) need the same three things: the connection/calendar pair, the seed built from what
// is already on screen, and an anchor snapshotted from the element's rect — snapshotted
// rather than a live ref because FullCalendar re-creates those nodes on every data update.
export const useOpenExternalEventPopup = (
  containerRef: React.RefObject<HTMLElement | null>,
  target: ExternalEventPopupTarget,
) => {
  const openEditEvent = useOpenEditEventPopup();
  const {
    externalId,
    title,
    allDay,
    start,
    end,
    connectionId,
    calendarId,
    url,
    preview,
  } = target;

  const eventTarget = getExternalCalendarEventRef({
    eventId: externalId,
    connectionId,
    calendarId,
  });

  const onOpen = useCallback(() => {
    if (!eventTarget || !start) {
      return;
    }
    const rect = containerRef.current?.getBoundingClientRect();
    openEditEvent(eventTarget, {
      anchor: { top: rect?.top ?? 0, left: rect?.right ?? 0 },
      url,
      preview,
      seed: buildEventFormSeed({
        title,
        allDay,
        start: parseEventBoundary(start, allDay),
        end: end ? parseEventBoundary(end, allDay) : null,
        calendar: {
          connectionId: eventTarget.connectionId,
          calendarId: eventTarget.calendarId,
        },
        preview,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- eventTarget is rebuilt each render from the primitives below
  }, [
    allDay,
    calendarId,
    connectionId,
    containerRef,
    end,
    externalId,
    openEditEvent,
    preview,
    start,
    title,
    url,
  ]);

  return { eventTarget, onOpen };
};
